/**
 * Pure reducer for the Work Items Dashboard page. All transitions are
 * immutable so the reducer can be property-tested directly.
 */

import { WORK_ITEMS_PAGE_SIZE } from "../../utils/constants";
import {
    ActiveFilter,
    WorkItemsAction,
    WorkItemsState,
    WorkItemsStatus,
} from "./model";

export const initialWorkItemsState = (
    activeFilter: ActiveFilter,
): WorkItemsState => ({
    activeFilter,
    selectedProcessPath: null,
    currentPage: 1,
    pageCache: {},
    forwardBuffer: [],
    nextToken: null,
    hasMore: false,
    counts: null,
    status: WorkItemsStatus.IDLE,
    errorMessage: null,
    sort: { column: null, direction: null },
});

export const workItemsReducer = (
    state: WorkItemsState,
    action: WorkItemsAction,
): WorkItemsState => {
    switch (action.type) {
        // Counts lifecycle ---------------------------------------------------
        case "COUNTS_REQUEST":
            return {
                ...state,
                status: WorkItemsStatus.LOADING_COUNTS,
                errorMessage: null,
            };

        case "COUNTS_SUCCESS":
            // The hosting component observes this transition and dispatches
            // ITEMS_REQUEST for page 1, which flips status to loading_items.
            return {
                ...state,
                counts: action.counts,
                errorMessage: null,
            };

        case "COUNTS_FAILURE":
            // Clear counts alongside the error so stale values are never shown.
            return {
                ...state,
                counts: null,
                status: WorkItemsStatus.ERROR,
                errorMessage: action.message,
            };

        // Items lifecycle ----------------------------------------------------
        case "ITEMS_REQUEST":
            return {
                ...state,
                status: WorkItemsStatus.LOADING_ITEMS,
                errorMessage: null,
            };

        case "ITEMS_SUCCESS": {
            // Fill the current page, then park overflow in the forward buffer.
            const combined = [...state.forwardBuffer, ...action.items];
            const rendered = combined.slice(0, WORK_ITEMS_PAGE_SIZE);
            const newBuffer = combined.slice(WORK_ITEMS_PAGE_SIZE);

            return {
                ...state,
                pageCache: {
                    ...state.pageCache,
                    [state.currentPage]: rendered,
                },
                forwardBuffer: newBuffer,
                nextToken: action.nextToken,
                hasMore: newBuffer.length > 0 || action.nextToken != null,
                status: WorkItemsStatus.READY,
                errorMessage: null,
            };
        }

        case "ITEMS_FAILURE":
            // Leave pageCache / forwardBuffer untouched so previously
            // rendered pages remain viewable.
            return {
                ...state,
                status: WorkItemsStatus.ERROR,
                errorMessage: action.message,
            };

        // Pagination ---------------------------------------------------------
        case "NAVIGATE_NEXT": {
            const destination = state.currentPage + 1;

            // Case A: destination already cached — no API call.
            if (state.pageCache[destination] !== undefined) {
                return { ...state, currentPage: destination };
            }

            // Case B: buffer has enough to fill the destination page.
            if (state.forwardBuffer.length >= WORK_ITEMS_PAGE_SIZE) {
                const rendered = state.forwardBuffer.slice(
                    0,
                    WORK_ITEMS_PAGE_SIZE,
                );
                const newBuffer = state.forwardBuffer.slice(
                    WORK_ITEMS_PAGE_SIZE,
                );
                return {
                    ...state,
                    currentPage: destination,
                    pageCache: {
                        ...state.pageCache,
                        [destination]: rendered,
                    },
                    forwardBuffer: newBuffer,
                    hasMore:
                        newBuffer.length > 0 || state.nextToken != null,
                };
            }

            // Case B2: no more to fetch but buffer has a partial last page.
            // Render the remaining buffer as the destination page and drain
            // it. Without this branch, overflow from an ITEMS_SUCCESS that
            // returned >PAGE_SIZE items with nextToken=null would be
            // unreachable.
            if (
                state.forwardBuffer.length > 0 &&
                state.nextToken == null
            ) {
                return {
                    ...state,
                    currentPage: destination,
                    pageCache: {
                        ...state.pageCache,
                        [destination]: state.forwardBuffer,
                    },
                    forwardBuffer: [],
                    hasMore: false,
                };
            }

            // Case C: fetch more. Advance currentPage and flip to
            // loading_items so the effect layer issues the API call; on
            // success, ITEMS_SUCCESS caches the rendered items.
            if (state.nextToken != null) {
                return {
                    ...state,
                    currentPage: destination,
                    status: WorkItemsStatus.LOADING_ITEMS,
                    errorMessage: null,
                };
            }

            // Case D: end of data.
            return state;
        }

        case "NAVIGATE_PREV": {
            if (state.currentPage <= 1) {
                return state;
            }
            // pageCache[currentPage - 1] is guaranteed to exist because the
            // user reached the current page by passing through it.
            return { ...state, currentPage: state.currentPage - 1 };
        }

        // Filter changes -----------------------------------------------------
        case "SELECT_PROCESS_PATH": {
            // Clicking the currently-selected card clears the filter.
            if (action.name === state.selectedProcessPath) {
                return workItemsReducer(state, { type: "CLEAR_FILTER" });
            }

            return {
                ...state,
                selectedProcessPath: action.name,
                activeFilter: {
                    ...state.activeFilter,
                    processPaths: [action.name],
                },
                currentPage: 1,
                pageCache: {},
                forwardBuffer: [],
                nextToken: null,
                hasMore: false,
                status: WorkItemsStatus.LOADING_ITEMS,
                errorMessage: null,
            };
        }

        case "CLEAR_FILTER":
            return {
                ...state,
                selectedProcessPath: null,
                activeFilter: {
                    ...state.activeFilter,
                    processPaths: [],
                },
                currentPage: 1,
                pageCache: {},
                forwardBuffer: [],
                nextToken: null,
                hasMore: false,
                status: WorkItemsStatus.LOADING_ITEMS,
                errorMessage: null,
            };

        // Refresh preserves selectedProcessPath and activeFilter; clears
        // counts / buffer / cache / token.
        case "REFRESH":
            return {
                ...state,
                currentPage: 1,
                pageCache: {},
                forwardBuffer: [],
                nextToken: null,
                hasMore: false,
                counts: null,
                status: WorkItemsStatus.LOADING_COUNTS,
                errorMessage: null,
            };

        // Warehouse change (e.g. user picks a different station from the
        // header dropdown). Treat it as a hard reset: rebind activeFilter
        // to the new warehouseId, drop the active process-path filter,
        // clear counts / buffer / cache / token, and flip to loading_counts
        // so the effect layer re-issues both fetches.
        //
        // No-op when the incoming warehouseId is empty or matches the
        // current value — guards against redundant resets when the
        // resolver returns the same id across renders.
        case "SET_WAREHOUSE_ID": {
            if (
                action.warehouseId === "" ||
                action.warehouseId === state.activeFilter.warehouseId
            ) {
                return state;
            }
            return {
                ...state,
                activeFilter: {
                    ...state.activeFilter,
                    warehouseId: action.warehouseId,
                    processPaths: [],
                },
                selectedProcessPath: null,
                currentPage: 1,
                pageCache: {},
                forwardBuffer: [],
                nextToken: null,
                hasMore: false,
                counts: null,
                status: WorkItemsStatus.LOADING_COUNTS,
                errorMessage: null,
            };
        }

        // Page-local sort — three-state cycle per column:
        //   none → asc → desc → none
        // A different column clicked at any stage resets to that column's asc.
        // Sort does not reset pagination/buffer/cache/token.
        case "TOGGLE_SORT": {
            const current = state.sort;
            const clickingSameColumn = current.column === action.column;

            let next: WorkItemsState["sort"];
            if (!clickingSameColumn) {
                next = { column: action.column, direction: "asc" };
            } else if (current.direction === "asc") {
                next = { column: action.column, direction: "desc" };
            } else if (current.direction === "desc") {
                next = { column: null, direction: null };
            } else {
                // Defensive: start the cycle if direction is null but column
                // matches (should not happen in practice).
                next = { column: action.column, direction: "asc" };
            }

            return { ...state, sort: next };
        }

        default: {
            // Exhaustiveness check — every action variant is handled above.
            const _exhaustive: never = action;
            void _exhaustive;
            return state;
        }
    }
};
